import { z } from "zod";

/* ===========================================================================
 * US-E18.F3-0196 (@UC9279–@UC9283) — FX / ĐA TIỀN TỆ.
 *
 * ⚠️ ADR 0005 (PO CHỐT hướng 1): **QUY ĐỔI Ở TẦNG NHẬP LIỆU, LEDGER GIỮ ĐƠN-CURRENCY.**
 *   Khoản chi ngoại tệ được quy đổi về tiền CỦA SỔ ngay lúc ghi ⇒ CHỈ số đã quy đổi vào ledger.
 *   Tỷ giá + số ngoại tệ gốc = METADATA (hiển thị/đối chiếu), KHÔNG phải line loại tiền khác
 *   ⇒ currency-mirror (CHECK/FK/trigger fund→account→line) KHÔNG THÁO GÌ CẢ.
 *
 * POST /v1/funds/:id/fx-rates   — khai tỷ giá (owner/admin/treasurer). Tỷ giá CÓ DẤU VẾT.
 * GET  /v1/funds/:id/fx-rates   — xem tỷ giá nhóm (mọi thành viên — minh bạch).
 * POST /v1/funds/:id/fx-spend   — ghi khoản CHI bằng ngoại tệ (quy đổi → post EXPENSE đơn-currency).
 *
 * INVARIANTS:
 *  - B1 tiền BigInt: mọi amount = STRING minor (^\d+$). KHÔNG float, KHÔNG number.
 *  - 🔴 Tỷ giá cũng KHÔNG float: client gửi CHUỖI thập phân ("24500.5"), server parse → BigInt
 *    scaled ×10^8 (`parseFxRateToScaled` — domain). Response trả `rateScaled` STRING.
 *  - Thiếu tỷ giá ⇒ 422 `FX_RATE_MISSING` (fail-closed — KHÔNG bịa 1:1).
 *  - `.strict()` chặn over-post (fundId/currency/groupId server set).
 * ========================================================================= */

const INTEGER_STRING_PATTERN = /^\d+$/;
/** Tỷ giá: chuỗi thập phân dương, ≤ 8 chữ số thập phân (thang FX_RATE_SCALE). KHÔNG số mũ, KHÔNG âm. */
const FX_RATE_DECIMAL_PATTERN = /^\d+(\.\d{1,8})?$/;
const ISO_CURRENCY_PATTERN = /^[A-Z]{3}$/;
const PG_BIGINT_MAX = 9223372036854775807n;
const AMOUNT_MAX_DIGITS = 19;
const RATE_MAX_LENGTH = 30;
const SOURCE_MAX = 100;
const MEMO_MAX = 500;
const IDEMPOTENCY_KEY_MAX = 200;
const RATES_LIST_MAX = 200;

export const FX_AMOUNT_INVALID_MESSAGE = "Số tiền phải là số nguyên dương (đơn vị nhỏ nhất)";
export const FX_RATE_INVALID_MESSAGE =
  "Tỷ giá phải là số dương dạng thập phân (tối đa 8 chữ số sau dấu chấm)";
export const FX_CURRENCY_INVALID_MESSAGE = "Mã tiền tệ phải theo ISO 4217 (3 chữ in hoa, vd USD)";

/** 🔴 Mã lỗi 422 khi KHÔNG có tỷ giá hiệu lực cho cặp tiền — fail-closed, KHÔNG bịa tỷ giá. */
export const FX_RATE_MISSING_CODE = "FX_RATE_MISSING" as const;
/** Mã lỗi 422 khi tỷ giá sai định dạng / ≤ 0. */
export const FX_RATE_INVALID_CODE = "FX_RATE_INVALID" as const;
/** Mã lỗi 422 khi số ngoại tệ quy đổi ra ≤ 0 (tiền không được "bốc hơi" âm thầm). */
export const FX_AMOUNT_INVALID_CODE = "FX_AMOUNT_INVALID" as const;

/** Chính sách chốt tỷ giá — whitelist = `packages/domain/src/fx.ts` (1 nguồn sự thật). */
export const FX_RATE_POLICY = {
  AT_SPEND: "at_spend",
  AT_SETTLEMENT: "at_settlement",
} as const;

export type FxRatePolicyValue = (typeof FX_RATE_POLICY)[keyof typeof FX_RATE_POLICY];

export const fxRatePolicySchema = z.enum([
  FX_RATE_POLICY.AT_SPEND,
  FX_RATE_POLICY.AT_SETTLEMENT,
]);

/** Khoá config FX trong registry `system_settings`. */
export const FX_RATE_POLICY_KEY = "fx_rate_policy" as const;

const isoCurrency = z
  .string()
  .trim()
  .length(3, FX_CURRENCY_INVALID_MESSAGE)
  .regex(ISO_CURRENCY_PATTERN, FX_CURRENCY_INVALID_MESSAGE);

/** Tiền STRING minor > 0, ≤ trần PG BIGINT (B1 — không float). */
const amountMinorString = z
  .string()
  .trim()
  .min(1, FX_AMOUNT_INVALID_MESSAGE)
  .max(AMOUNT_MAX_DIGITS, FX_AMOUNT_INVALID_MESSAGE)
  .regex(INTEGER_STRING_PATTERN, FX_AMOUNT_INVALID_MESSAGE)
  .refine((v) => {
    try {
      const n = BigInt(v);
      return n > 0n && n <= PG_BIGINT_MAX;
    } catch {
      return false;
    }
  }, FX_AMOUNT_INVALID_MESSAGE);

/** Tỷ giá CHUỖI thập phân (server parse → BigInt scaled ×10^8). KHÔNG z.number() (chống float). */
const fxRateDecimalString = z
  .string()
  .trim()
  .min(1, FX_RATE_INVALID_MESSAGE)
  .max(RATE_MAX_LENGTH, FX_RATE_INVALID_MESSAGE)
  .regex(FX_RATE_DECIMAL_PATTERN, FX_RATE_INVALID_MESSAGE)
  // Tỷ giá 0 (kể cả "0", "0.0", "0.00000000") ⇒ vô nghĩa: mọi chữ số đều 0 ⇒ reject (fail-closed).
  .refine((v) => /[1-9]/.test(v), FX_RATE_INVALID_MESSAGE);

/* ---------------------------------------------------------------------------
 * S01 — QUẢN LÝ TỶ GIÁ (dấu vết: source + effectiveAt) · @UC9280
 * ------------------------------------------------------------------------- */

/** Body POST /v1/funds/:id/fx-rates — khai 1 mốc tỷ giá (append-only: mỗi lần đổi = 1 hàng mới). */
export const createFxRateRequestSchema = z
  .object({
    /** Tiền NGOẠI TỆ (nguồn) — vd USD. */
    baseCurrency: isoCurrency,
    /** Tiền SỔ (đích) — vd VND. Phải khác baseCurrency. */
    quoteCurrency: isoCurrency,
    /** 1 baseCurrency = <rate> quoteCurrency. CHUỖI thập phân (no-float). */
    rate: fxRateDecimalString,
    /** Tỷ giá có hiệu lực TỪ lúc nào (dấu vết thời gian). */
    effectiveAt: z.string().datetime(),
    /** Nguồn tỷ giá (dấu vết: ai/đâu cung cấp) — manual | vietcombank | ecb… */
    source: z.string().trim().min(1).max(SOURCE_MAX),
  })
  .strict()
  .refine((v) => v.baseCurrency !== v.quoteCurrency, {
    message: "baseCurrency và quoteCurrency phải khác nhau.",
    path: ["quoteCurrency"],
  });

export type CreateFxRateRequest = z.infer<typeof createFxRateRequestSchema>;

export const fxRateSchema = z
  .object({
    id: z.string().uuid(),
    baseCurrency: z.string().length(3),
    quoteCurrency: z.string().length(3),
    /** 🔴 Tỷ giá × 10^8 (BigInt → STRING). KHÔNG float. */
    rateScaled: z.string().regex(INTEGER_STRING_PATTERN),
    /** Hiển thị thập phân (derived từ rateScaled — không phải nguồn sự thật). */
    rate: z.string(),
    effectiveAt: z.string().datetime(),
    source: z.string(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type FxRateDto = z.infer<typeof fxRateSchema>;

/** GET /v1/funds/:id/fx-rates — tỷ giá của NHÓM sở hữu sổ (mới nhất trước). */
export const listFxRatesResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    /** Đồng tiền CỦA SỔ (đích quy đổi — ledger chỉ ghi tiền này). */
    fundCurrency: z.string().length(3),
    /** Chính sách chốt tỷ giá HIỆU LỰC của sổ (fund > group > system > default). */
    fxRatePolicy: fxRatePolicySchema,
    rates: z.array(fxRateSchema).max(RATES_LIST_MAX),
  })
  .strict();

export type ListFxRatesResponse = z.infer<typeof listFxRatesResponseSchema>;

/* ---------------------------------------------------------------------------
 * S03 — GHI KHOẢN CHI NGOẠI TỆ (quy đổi tầng nhập liệu) · @UC9282 @UC9283
 * ------------------------------------------------------------------------- */

/**
 * Body POST /v1/funds/:id/fx-spend — ghi khoản CHI bằng ngoại tệ.
 *
 * `originalAmountMinor` là minor units của `originalCurrency` (100 USD = "10000" cents).
 * Server quy đổi → tiền CỦA SỔ → post 1 tx EXPENSE **đơn-currency** (DEBIT EXPENSE / CREDIT CASH).
 * `originalCurrency` == tiền sổ ⇒ KHÔNG quy đổi, KHÔNG cần tỷ giá (đường cũ, 0 regress).
 */
export const fxSpendRequestSchema = z
  .object({
    originalAmountMinor: amountMinorString,
    originalCurrency: isoCurrency,
    /** Thời điểm CHI (dùng cho policy at_spend). Vắng ⇒ now. */
    spentAt: z.string().datetime().optional(),
    /** Bắt buộc — double-submit ⇒ đúng 1 tx (B4 idempotency). */
    idempotencyKey: z.string().trim().min(1).max(IDEMPOTENCY_KEY_MAX),
    memo: z.string().trim().max(MEMO_MAX).optional(),
  })
  .strict();

export type FxSpendRequest = z.infer<typeof fxSpendRequestSchema>;

/**
 * Kết quả ghi chi ngoại tệ. 🔴 `convertedAmountMinor` (tiền SỔ) là con số ĐI VÀO LEDGER;
 * `originalAmountMinor`/`originalCurrency`/`fxRateScaled`/`fxRateId` chỉ là METADATA.
 */
export const fxSpendResponseSchema = z
  .object({
    ledgerTxId: z.string().uuid(),
    fundId: z.string().uuid(),
    /** Đồng tiền CỦA SỔ — MỌI ledger_line của tx mang currency này (đơn-currency). */
    currency: z.string().length(3),
    /** 🔴 Số tiền ĐÃ QUY ĐỔI — con số THẬT trong sổ. */
    convertedAmountMinor: z.string().regex(INTEGER_STRING_PATTERN),
    /** METADATA — số ngoại tệ gốc (KHÔNG vào ledger line). */
    originalAmountMinor: z.string().regex(INTEGER_STRING_PATTERN),
    originalCurrency: z.string().length(3),
    /** METADATA — tỷ giá đã dùng × 10^8; null khi KHÔNG-FX (cùng tiền sổ). */
    fxRateScaled: z.string().regex(INTEGER_STRING_PATTERN).nullable(),
    fxRate: z.string().nullable(),
    /** FK hàng tỷ giá đã dùng (dấu vết); null khi KHÔNG-FX. */
    fxRateId: z.string().uuid().nullable(),
    fxRateAt: z.string().datetime().nullable(),
    /** Chính sách đã dùng để CHỌN tỷ giá (consumer thật của config). */
    fxRatePolicy: fxRatePolicySchema,
    spentAt: z.string().datetime(),
    recordedAt: z.string().datetime(),
  })
  .strict();

export type FxSpendResponse = z.infer<typeof fxSpendResponseSchema>;
